import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import RouteTracker from './components/RouteTracker'
import LoginPage, { loginContext } from './pages/public/Login'
import LogoutPage from './pages/public/Logout'
import NotFoundPage from './pages/public/NotFound'
import UpdatePasswordPage, { updatePasswordContext } from './pages/public/UpdatePassword'
import AuthenticatedLayout from './layouts/AuthenticatedLayout'
import SplitScreenLayout from './layouts/SplitScreenLayout'
import RequestPasswordReset, { requestPasswordResetContext } from './pages/public/RequestPasswordReset'
import AdminDashboardPage from './pages/admin/Dashboard'
import CustomerDashboardPage from './pages/customer/Dashboard'
import JobsPage from './pages/admin/Jobs'
import NotificationsPage from './pages/admin/Notifications'
import RolesPage from './pages/admin/Roles'
import CustomersPage from './pages/admin/Customers'
import UsersPage from './pages/admin/Users'
import ReportsPage from './pages/admin/Reports'
import AppLaunchesPage from './pages/admin/AppLaunches'
import AuditLogsPage from './pages/admin/AuditLogs'
import ProtectedRoute from './components/ProtectedRoute'
import AdminPermissionRoute from './components/AdminPermissionRoute'
import { AccountRole } from '@ivy-monorepo/shared'
import EmailConfirmation, { emailConfirmationContext } from './pages/public/EmailConfirmation'
import VenuesPage from './pages/admin/Venues'
import VenueDetailPage from './pages/admin/VenueDetail'
import CommentsPage from './pages/admin/Comments'
import TermsOfUsePage, { termsOfUseContext } from './pages/public/TermsOfUse'
import PrivacyPolicyPage, { privacyPolicyContext } from './pages/public/PrivacyPolicy'
import VenueActivityPage from './pages/admin/VenueActivity'
import VenueEventsPage from './pages/admin/VenueEvents'
import EventbritePage from './pages/admin/Eventbrite'
import RedirectToVenueActivity from './components/RedirectToVenueActivity'
import UserInteractionsPage from './pages/admin/UserInteractions'
import RedirectToUserInteractions from './components/RedirectToUserInteractions'
import LandingPageV2 from './pages/landing/v2/LandingPageV2'
import DeleteMePage, { deleteMeContext } from './pages/public/DeleteMe'
import ChildSafetyPage, { childSafetyContext } from './pages/public/ChildSafety'
import CustomerVenuesPage from './pages/customer/Venues'
import CustomerVenuePage from './pages/customer/Venue'
import AdminMapPage from './pages/admin/Map'
import LiveMapPage from './pages/admin/LiveMap'
import UserDetailPage from './pages/admin/UserDetail'
import FeatureFlagsPage from './pages/admin/FeatureFlags'
import DevTelephonesPage from './pages/admin/DevTelephones'
import SimulationPage from './pages/admin/Simulation'
import IntentionsPage from './pages/admin/Intentions'
import SystemStatusPage from './pages/admin/SystemStatus'
import PhotoVerificationPage from './pages/admin/PhotoVerification'
import StickersPage from './pages/admin/Stickers'
import TicketsPage from './pages/admin/Tickets'
import PartneredHotspotsPage from './pages/admin/PartneredHotspots'
import EventJourneyPage from './pages/admin/EventJourney'
import ReactModal from 'react-modal'

ReactModal.setAppElement('#root')

function RedirectAdminDetailToUserDetail() {
  const { publicId } = useParams<{ publicId: string }>()
  return <Navigate to={`/admin/users/${publicId}`} replace />
}

/** Old landing URLs (/new, /old, …) → home, keeping the query string (OneLink `?source=` attribution). */
const LegacyLandingRedirect = () => {
  const { search } = useLocation()
  return <Navigate to={`/${search}`} replace />
}

function App() {
  return (
    <Router>
      <RouteTracker />
      <div>
        <Routes>
          {/* Public routes with SplitScreenLayout */}
          <Route path="/login" element={<SplitScreenLayout context={loginContext} />}>
            <Route index element={<LoginPage />} />
          </Route>

          <Route path="/email-confirmation" element={<SplitScreenLayout context={emailConfirmationContext} />}>
            <Route index element={<EmailConfirmation />} />
          </Route>

          <Route path="/request-password-reset" element={<SplitScreenLayout context={requestPasswordResetContext} />}>
            <Route index element={<RequestPasswordReset />} />
          </Route>

          <Route path="/update-password" element={<SplitScreenLayout context={updatePasswordContext} />}>
            <Route index element={<UpdatePasswordPage />} />
          </Route>

          <Route path="/delete-me" element={<SplitScreenLayout context={deleteMeContext} />}>
            <Route index element={<DeleteMePage />} />
          </Route>
          <Route path="/child-safety" element={<SplitScreenLayout context={childSafetyContext} />}>
            <Route index element={<ChildSafetyPage />} />
          </Route>
          <Route path="/terms-of-use" element={<SplitScreenLayout context={termsOfUseContext} />}>
            <Route index element={<TermsOfUsePage />} />
          </Route>
          <Route path="/privacy-policy" element={<SplitScreenLayout context={privacyPolicyContext} />}>
            <Route index element={<PrivacyPolicyPage />} />
          </Route>

          <Route path="/logout" element={<LogoutPage />} />

          <Route
            element={
              <ProtectedRoute requiredRole={AccountRole.Admin}>
                <AuthenticatedLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin/dashboard" element={<AdminPermissionRoute><AdminDashboardPage /></AdminPermissionRoute>} />
            <Route path="/admin/admins/:publicId" element={<RedirectAdminDetailToUserDetail />} />
            <Route path="/admin/admins" element={<Navigate to="/admin/users?view=admins" replace />} />
            <Route path="/admin/roles" element={<AdminPermissionRoute><RolesPage /></AdminPermissionRoute>} />
            <Route path="/admin/users" element={<AdminPermissionRoute><UsersPage /></AdminPermissionRoute>} />
            <Route path="/admin/venues" element={<AdminPermissionRoute><VenuesPage /></AdminPermissionRoute>} />
            <Route path="/admin/venues/:publicId" element={<AdminPermissionRoute><VenueDetailPage /></AdminPermissionRoute>} />
            <Route path="/admin/venue-activity" element={<AdminPermissionRoute><VenueActivityPage /></AdminPermissionRoute>} />
            <Route path="/admin/venue-events" element={<AdminPermissionRoute><VenueEventsPage /></AdminPermissionRoute>} />
            <Route path="/admin/eventbrite" element={<AdminPermissionRoute><EventbritePage /></AdminPermissionRoute>} />
            <Route path="/admin/venue-events/:publicId/journey" element={<AdminPermissionRoute><EventJourneyPage /></AdminPermissionRoute>} />
            <Route path="/admin/breadcrumbs" element={<RedirectToVenueActivity view="breadcrumbs" />} />
            <Route path="/admin/stickers" element={<AdminPermissionRoute><StickersPage /></AdminPermissionRoute>} />
            <Route path="/admin/tickets" element={<AdminPermissionRoute><TicketsPage /></AdminPermissionRoute>} />
            <Route path="/admin/partnered-hotspots" element={<AdminPermissionRoute><PartneredHotspotsPage /></AdminPermissionRoute>} />
            <Route path="/admin/customers" element={<AdminPermissionRoute><CustomersPage /></AdminPermissionRoute>} />
            <Route path="/admin/accounts/authenticated" element={<Navigate to="/admin/users?view=logins" replace />} />
            <Route path="/admin/app-launches" element={<AdminPermissionRoute><AppLaunchesPage /></AdminPermissionRoute>} />
            <Route path="/admin/audit-logs" element={<AdminPermissionRoute><AuditLogsPage /></AdminPermissionRoute>} />
            <Route path="/admin/jobs" element={<AdminPermissionRoute><JobsPage /></AdminPermissionRoute>} />
            <Route path="/admin/system-status" element={<AdminPermissionRoute><SystemStatusPage /></AdminPermissionRoute>} />
            <Route path="/admin/photo-verification" element={<AdminPermissionRoute><PhotoVerificationPage /></AdminPermissionRoute>} />
            <Route path="/admin/notifications" element={<AdminPermissionRoute><NotificationsPage /></AdminPermissionRoute>} />
            <Route path="/admin/comments" element={<AdminPermissionRoute><CommentsPage /></AdminPermissionRoute>} />
            <Route path="/admin/user-interactions" element={<AdminPermissionRoute><UserInteractionsPage /></AdminPermissionRoute>} />
            <Route path="/admin/blocks" element={<RedirectToUserInteractions view="blocks" />} />
            <Route path="/admin/conversations" element={<RedirectToUserInteractions view="conversations" />} />
            <Route path="/admin/messages" element={<RedirectToUserInteractions view="conversations" />} />
            <Route path="/admin/likes" element={<RedirectToUserInteractions view="likes" />} />
            <Route path="/admin/visits" element={<RedirectToVenueActivity view="visits" />} />
            <Route path="/admin/map" element={<AdminPermissionRoute><AdminMapPage /></AdminPermissionRoute>} />
            <Route path="/admin/live-map" element={<AdminPermissionRoute><LiveMapPage /></AdminPermissionRoute>} />
            <Route path="/admin/feature-flags" element={<AdminPermissionRoute><FeatureFlagsPage /></AdminPermissionRoute>} />
            <Route path="/admin/dev-telephones" element={<AdminPermissionRoute><DevTelephonesPage /></AdminPermissionRoute>} />
            <Route path="/admin/simulation" element={<AdminPermissionRoute><SimulationPage /></AdminPermissionRoute>} />
            <Route path="/admin/intentions" element={<AdminPermissionRoute><IntentionsPage /></AdminPermissionRoute>} />
            <Route path="/admin/reports" element={<AdminPermissionRoute><ReportsPage /></AdminPermissionRoute>} />
            <Route path="/admin/checkedin-users" element={<Navigate to="/admin/users?view=checked-in" replace />} />
            <Route path="/admin/users/:publicId" element={<AdminPermissionRoute><UserDetailPage /></AdminPermissionRoute>} />
          </Route>

          <Route
            element={
              <ProtectedRoute requiredRole={AccountRole.Customer}>
                <AuthenticatedLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/customer/dashboard" element={<CustomerDashboardPage />} />
            <Route path="/customer/venues" element={<CustomerVenuesPage />} />
            <Route path="/customer/venues/:publicId" element={<CustomerVenuePage />} />
          </Route>

          {/* Landing page — the former /new design; old landing URLs redirect here */}
          <Route path="/" element={<LandingPageV2 />} />
          <Route path="/new" element={<LegacyLandingRedirect />} />
          <Route path="/old" element={<LegacyLandingRedirect />} />
          <Route path="/old-video-still" element={<LegacyLandingRedirect />} />

          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </Router>
  )
}

export default App
